import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Upload, FileText, ArrowRight, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMP_TYPES = ["Full Time", "Part Time", "Remote", "Internship"];

const initial = {
  full_name: "", email: "", mobile: "", city: "", state: "",
  qualification: "", experience: "", current_title: "",
  preferred_role: "", preferred_location: "", expected_salary: "",
  employment_type: "Full Time", skills: "", message: "",
  agreed_terms: false,
};

export default function JobEnquiry() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...initial, preferred_role: params.get("job") || "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [trackUrl, setTrackUrl] = useState("");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Resume must be under 8MB");
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed_terms) return toast.error("Please agree to Terms & Privacy Policy");
    if (!resume) return toast.error("Please attach your resume");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
      fd.append("resume", resume);
      const { data } = await api.post("/enquiries", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Enquiry submitted");
      if (data.track_token) setTrackUrl(`${window.location.origin}/track/${data.track_token}`);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="enquiry-success-panel" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="inline-flex w-20 h-20 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center">
          <CheckCircle2 size={40} strokeWidth={2} />
        </motion.div>
        <h1 data-testid="enquiry-success" className="mt-6 text-3xl lg:text-4xl font-extrabold text-slate-900 font-display">Thank you!</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Your job enquiry has been submitted successfully. Our team will contact you soon with curated opportunities matching your profile.
        </p>
        {trackUrl && (
          <div className="mt-8 bg-indigo-50 border border-indigo-100 rounded-2xl p-5 text-left">
            <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700">Track your application</p>
            <p className="mt-1 text-sm text-slate-600">Save this private link to check your live status anytime.</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Input data-testid="track-link-input" readOnly value={trackUrl} className="bg-white text-sm" />
              <Button type="button" variant="outline" className="rounded-full" data-testid="copy-track-link" onClick={() => { navigator.clipboard?.writeText(trackUrl); toast.success("Link copied"); }}>
                <Copy size={14} className="mr-1.5" /> Copy
              </Button>
            </div>
          </div>
        )}
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/jobs"><Button variant="outline" className="rounded-full">Browse jobs</Button></Link>
          <Link to="/"><Button className="rounded-full bg-gradient-to-r from-blue-700 to-indigo-600 text-white">Back home <ArrowRight size={16} className="ml-1.5" /></Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Job Enquiry</p>
        <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">Tell us about you.</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Complete the form below and a career mentor will get back within 48 hours. All fields with * are required.
        </p>
      </div>

      <form onSubmit={submit} data-testid="enquiry-form" className="mt-10 space-y-8">
        {/* Personal */}
        <Section title="Personal details">
          <Field label="Full Name *"><Input data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} required /></Field>
          <Field label="Email Address *"><Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} required /></Field>
          <Field label="Mobile Number *"><Input data-testid="input-mobile" value={form.mobile} onChange={set("mobile")} required /></Field>
          <Field label="City *"><Input data-testid="input-city" value={form.city} onChange={set("city")} required /></Field>
          <Field label="State *"><Input data-testid="input-state" value={form.state} onChange={set("state")} required /></Field>
          <Field label="Highest Qualification *"><Input data-testid="input-qualification" value={form.qualification} onChange={set("qualification")} placeholder="e.g. B.Tech, MBA" required /></Field>
        </Section>

        {/* Professional */}
        <Section title="Professional background">
          <Field label="Total Work Experience *"><Input data-testid="input-experience" value={form.experience} onChange={set("experience")} placeholder="e.g. 3 years" required /></Field>
          <Field label="Current / Previous Job Title"><Input data-testid="input-current-title" value={form.current_title} onChange={set("current_title")} placeholder="e.g. Frontend Engineer" /></Field>
          <Field label="Skills *" full><Textarea data-testid="input-skills" value={form.skills} onChange={set("skills")} placeholder="e.g. React, TypeScript, GraphQL, Figma" rows={3} required /></Field>
        </Section>

        {/* Preferences */}
        <Section title="Job preferences">
          <Field label="Preferred Job Role *"><Input data-testid="input-preferred-role" value={form.preferred_role} onChange={set("preferred_role")} placeholder="e.g. Senior Product Designer" required /></Field>
          <Field label="Preferred Location *"><Input data-testid="input-preferred-location" value={form.preferred_location} onChange={set("preferred_location")} placeholder="e.g. Bengaluru / Remote" required /></Field>
          <Field label="Expected Salary *"><Input data-testid="input-expected-salary" value={form.expected_salary} onChange={set("expected_salary")} placeholder="e.g. ₹18L / year" required /></Field>
          <Field label="Employment Type *">
            <Select value={form.employment_type} onValueChange={set("employment_type")}>
              <SelectTrigger data-testid="select-employment-type" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </Section>

        {/* Resume + message */}
        <Section title="Resume & message">
          <div className="md:col-span-2">
            <Label className="text-sm font-medium text-slate-800">Resume Upload *</Label>
            <label
              htmlFor="resume-file"
              data-testid="resume-dropzone"
              className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-indigo-500 hover:bg-indigo-50/40 rounded-2xl p-8 cursor-pointer transition"
            >
              {resume ? (
                <>
                  <FileText size={26} className="text-indigo-600" />
                  <p className="mt-2 font-medium text-slate-800">{resume.name}</p>
                  <p className="text-xs text-slate-500">{(resume.size / 1024).toFixed(1)} KB · click to change</p>
                </>
              ) : (
                <>
                  <Upload size={24} className="text-indigo-600" />
                  <p className="mt-2 text-sm font-medium text-slate-800">Drop or click to upload</p>
                  <p className="text-xs text-slate-500">PDF, DOC or DOCX · max 8MB</p>
                </>
              )}
              <input id="resume-file" data-testid="input-resume" type="file" accept=".pdf,.doc,.docx" onChange={handleFile} className="hidden" />
            </label>
          </div>
          <Field label="Message / Additional Information" full>
            <Textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={4} placeholder="Anything else you&apos;d like us to know?" />
          </Field>
        </Section>

        <div className="flex items-start gap-3 bg-slate-50 rounded-xl border border-slate-200 p-4">
          <Checkbox id="agree" data-testid="checkbox-terms" checked={form.agreed_terms} onCheckedChange={(v) => setForm((s) => ({ ...s, agreed_terms: !!v }))} className="mt-0.5" />
          <Label htmlFor="agree" className="text-sm text-slate-700 leading-relaxed">
            I agree to CareerConnect's <Link to="/terms" className="text-indigo-700 underline">Terms</Link> &amp; <Link to="/privacy" className="text-indigo-700 underline">Privacy Policy</Link> and consent to being contacted about opportunities.
          </Label>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="submit-enquiry-button"
          size="lg"
          className="w-full sm:w-auto h-12 rounded-full px-10 bg-gradient-to-r from-blue-700 to-indigo-600 hover:from-blue-800 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/25"
        >
          {submitting ? "Submitting…" : "Submit Enquiry"}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
      <h2 className="text-lg font-semibold text-slate-900 font-display">{title}</h2>
      <div className="mt-6 grid md:grid-cols-2 gap-x-6 gap-y-5">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
